import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { publicAPI } from '../services/api';
import { 
  Building2, MapPin, Globe, Users, Calendar, Shield, Loader2,
  ExternalLink, BadgeCheck, Award, Crown, FileText, ChevronLeft,
  Briefcase, CheckCircle
} from 'lucide-react';

// Company tier badge component - VDS colors
const CompanyTierBadge = ({ tier, size = 'md' }) => {
  const tiers = {
    NONE: { icon: Shield, color: '#5B6472', name: 'None' },
    BASIC: { icon: Shield, color: '#5B6472', name: 'Basic' },
    VERIFIED_EMPLOYER: { icon: BadgeCheck, color: '#1B5AFF', name: 'Verified' },
    TRUSTED_EMPLOYER: { icon: Award, color: '#00C9A7', name: 'Trusted' },
    ELITE_EMPLOYER: { icon: Crown, color: '#F0B429', name: 'Elite' }
  };
  
  const tierInfo = tiers[tier?.current] || tiers.NONE;
  const Icon = tierInfo.icon;
  
  const sizeClasses = {
    sm: 'px-2 py-1 text-[9px]',
    md: 'px-3 py-1.5 text-[10px]',
    lg: 'px-4 py-2 text-xs'
  };
  
  return (
    <div 
      className={`inline-flex items-center gap-1.5 rounded-full border font-mono uppercase tracking-[0.12em] ${sizeClasses[size]}`}
      style={{ 
        backgroundColor: `${tierInfo.color}12`, 
        borderColor: `${tierInfo.color}40`,
        color: tierInfo.color
      }}
      data-testid="company-tier-badge"
    >
      <Icon className={size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} />
      <span className="font-medium">{tier?.name || tierInfo.name}</span>
    </div>
  );
};

const CompanyProfile = () => {
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCompanyProfile();
  }, [slug]);

  const fetchCompanyProfile = async () => {
    try {
      const res = await publicAPI.getCompanyProfile(slug);
      setCompany(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Company not found');
      } else {
        setError('Failed to load company profile');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F8FF' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#1B5AFF' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F5F8FF' }}>
        <Card style={{ background: '#FFFFFF', border: '1px solid #E8EDF5', borderRadius: '16px', padding: '32px', textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'rgba(229, 72, 77, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Building2 className="w-8 h-8" style={{ color: '#E5484D' }} />
          </div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#0A0F1E', fontFamily: "'Bricolage Grotesque', sans-serif", marginBottom: '8px' }}>{error}</h1>
          <p style={{ color: '#5B6472', marginBottom: '24px' }}>
            The company you're looking for doesn't exist or may have been removed.
          </p>
          <Link to="/">
            <Button style={{ background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)', color: '#FFFFFF', borderRadius: '10px' }}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Go to Homepage
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F5F8FF' }} data-testid="company-profile-page">
      {/* Header/Navigation */}
      <header className="sticky top-0 z-50 backdrop-blur-xl" style={{ background: 'rgba(255, 255, 255, 0.85)', borderBottom: '1px solid #E8EDF5' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)' }}>
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <span style={{ fontSize: '20px', fontWeight: 800, color: '#0A0F1E', fontFamily: "'Bricolage Grotesque', sans-serif" }}>VETT</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login">
              <Button variant="ghost" style={{ color: '#5B6472', fontFamily: "'DM Mono', monospace", fontSize: '13px' }}>
                Sign In
              </Button>
            </Link>
            <Link to="/signup?type=employer" className="hidden sm:block">
              <Button style={{ background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)', color: '#FFFFFF', borderRadius: '10px', fontFamily: "'DM Mono', monospace", fontSize: '12px', fontWeight: 500 }}>
                Get Started
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Company Header */}
        <Card style={{ background: '#FFFFFF', border: '1px solid #E8EDF5', borderRadius: '16px', overflow: 'hidden', marginBottom: '24px' }}>
          {/* Banner area */}
          <div className="h-20 sm:h-32 relative" style={{ background: 'linear-gradient(120deg, rgba(27, 90, 255, 0.1), rgba(0, 201, 167, 0.1))' }}>
          </div>
          
          <div className="px-4 sm:px-8 pb-6 sm:pb-8">
            {/* Logo & Name */}
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-6 -mt-10 sm:-mt-12 relative z-10 mb-4 sm:mb-6">
              {company.logo_url ? (
                <img 
                  src={company.logo_url} 
                  alt={company.company_name}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover"
                  style={{ border: '4px solid #FFFFFF', background: '#F5F8FF' }}
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex items-center justify-center" style={{ background: '#F5F8FF', border: '4px solid #FFFFFF' }}>
                  <Building2 className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: '#5B6472' }} />
                </div>
              )}
              <div className="flex-1 sm:pb-2">
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#0A0F1E', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                    {company.company_name}
                  </h1>
                  <CompanyTierBadge tier={company.tier} size="sm" />
                </div>
                {company.industry && (
                  <p className="mt-1 flex items-center gap-2 text-sm" style={{ color: '#5B6472' }}>
                    <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {company.industry}
                  </p>
                )}
              </div>
            </div>

            {/* Description */}
            {company.description && (
              <p className="font-light leading-relaxed mb-4 sm:mb-6 max-w-3xl text-sm sm:text-base" style={{ color: '#0A0F1E' }}>
                {company.description}
              </p>
            )}

            {/* Meta Info */}
            <div className="flex flex-wrap gap-3 sm:gap-6 text-xs sm:text-sm">
              {company.location && (
                <div className="flex items-center gap-2" style={{ color: '#5B6472' }}>
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {company.location}
                </div>
              )}
              {company.company_size && (
                <div className="flex items-center gap-2" style={{ color: '#5B6472' }}>
                  <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {company.company_size} employees
                </div>
              )}
              {company.website && (
                <a 
                  href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 transition-colors"
                  style={{ color: '#1B5AFF' }}
                >
                  <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Website
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {/* Verified Records */}
          <Card style={{ background: '#FFFFFF', border: '1px solid #E8EDF5', borderRadius: '16px', padding: '20px' }}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0, 201, 167, 0.1)' }}>
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#00C9A7' }} />
              </div>
              <div>
                <p className="text-[9px] font-mono uppercase tracking-[0.18em] mb-1" style={{ color: '#5B6472' }}>
                  Verified Records
                </p>
                <p style={{ fontSize: '28px', fontWeight: 700, color: '#0A0F1E', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  {company.verified_records_count}
                </p>
              </div>
            </div>
          </Card>

          {/* Company Tier */}
          <Card style={{ background: '#FFFFFF', border: '1px solid #E8EDF5', borderRadius: '16px', padding: '20px' }}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div 
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${company.tier?.color || '#5B6472'}15` }}
              >
                <Shield className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: company.tier?.color || '#5B6472' }} />
              </div>
              <div>
                <p className="text-[9px] font-mono uppercase tracking-[0.18em] mb-1" style={{ color: '#5B6472' }}>
                  Company Tier
                </p>
                <p 
                  style={{ fontSize: '20px', fontWeight: 700, fontFamily: "'Bricolage Grotesque', sans-serif", color: company.tier?.color || '#5B6472' }}
                >
                  {company.tier?.name || 'None'}
                </p>
              </div>
            </div>
          </Card>

          {/* Member Since */}
          <Card style={{ background: '#FFFFFF', border: '1px solid #E8EDF5', borderRadius: '16px', padding: '20px' }}>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(27, 90, 255, 0.1)' }}>
                <Calendar className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#1B5AFF' }} />
              </div>
              <div>
                <p className="text-[9px] font-mono uppercase tracking-[0.18em] mb-1" style={{ color: '#5B6472' }}>
                  Member Since
                </p>
                <p style={{ fontSize: '18px', fontWeight: 700, color: '#0A0F1E', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  {company.member_since || 'N/A'}
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* Trust Banner */}
        <Card style={{ background: 'linear-gradient(120deg, rgba(27, 90, 255, 0.08), rgba(0, 201, 167, 0.08))', border: '1px solid rgba(27, 90, 255, 0.2)', borderRadius: '16px', padding: '24px' }}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(27, 90, 255, 0.1)' }}>
              <FileText className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#1B5AFF' }} />
            </div>
            <div className="flex-1">
              <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0A0F1E', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                VETT Verified Employer
              </h3>
              <p className="text-sm sm:text-base" style={{ color: '#5B6472' }}>
                This company uses VETT to create verified employment records for their team members.
              </p>
            </div>
            <Link to="/signup?type=employee" className="w-full sm:w-auto">
              <Button style={{ background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)', color: '#FFFFFF', borderRadius: '10px', fontFamily: "'DM Mono', monospace", fontSize: '12px', fontWeight: 500, width: '100%' }}>
                Get Your VETT ID
              </Button>
            </Link>
          </div>
        </Card>

        {/* Share Link Info */}
        <div className="mt-6 sm:mt-8 text-center">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: '#5B6472' }}>
            Share this company profile
          </p>
          <p className="text-xs sm:text-sm mt-1 break-all" style={{ color: '#5B6472' }}>
            {window.location.origin}/company/{company.slug}
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 sm:py-8 mt-12 sm:mt-16" style={{ borderTop: '1px solid #E8EDF5' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs sm:text-sm" style={{ color: '#5B6472' }}>
            &copy; {new Date().getFullYear()} VETT. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default CompanyProfile;
